import React, { useEffect, useRef } from 'react';
import { t } from 'ttag';
import { useArrowFocusList } from '~/lib/a11y/useArrowFocusList';
import { useFocusTrap } from '~/lib/a11y/useFocusTrap';
import { Card } from '~/ui/card/Card';
import { Flex } from '~/ui/flex/Flex';
import { Overlay } from '~/ui/overlay/Overlay';
import { withTransition } from '~/ui/transition/Transition';
import { TextLocalePage } from './locales/TextLocalePage';
import { PlaybackSpeed } from './playback-speed/PlaybackSpeed';
import { Root } from './Root';
import { OpenSettingsButton } from './OpenSettingsButton';
import { SettingsPages, SettingsProvider, useSettingsStore } from './Store';
import { MediumLocalePage } from './locales/MediumLocalePage';
import { useOpenCloseAudioEffect } from '~/ui/audio-effect/useOpenCloseAudioEffect';
import { DarkModePage } from './locales/DarkModePage';
import { SubtitleLocalePage } from './locales/SubtitleLocalePage';
const SettingsCard = withTransition(Card);

const SettingsUI = () => {
  const settingsDialogRef = useRef<HTMLDivElement>(null);
  const settingsTriggerRef = useRef<HTMLButtonElement>(null);
  const { open, toggle, page, setPage } = useSettingsStore();
  useOpenCloseAudioEffect(open);
  useFocusTrap(settingsDialogRef, open, undefined, settingsTriggerRef);
  useArrowFocusList(settingsDialogRef, open);

  useEffect(() => {
    if (!open) return;

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;

      // Sub-pages: Escape acts like Back. Root: close settings.
      if (page !== SettingsPages.Root) {
        setPage(SettingsPages.Root);
        return;
      }

      toggle(false);
    };

    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [open, page, setPage, toggle]);

  return (
    <>
      <OpenSettingsButton ref={settingsTriggerRef} />
      <Overlay open={open} allowScroll onClick={() => toggle(false)}>
        <Flex
          zIndex="dialogs"
          p={{ _: 'sm', sm: 'xl' }}
          minHeight="100%"
          justifyContent={{ _: 'stretch', sm: 'flex-end' }}
          alignItems={{ _: 'flex-end', sm: 'flex-start' }}
          overflow="hidden"
        >
          <SettingsCard show={open} w={{ _: '100%', sm: 'auto' }}>
            <div
              ref={settingsDialogRef}
              role="dialog"
              aria-modal="true"
              aria-label={t`Accessibility Settings`}
            >
              <Root />
              <MediumLocalePage />
              <TextLocalePage />
              <SubtitleLocalePage />
              <DarkModePage />
              <PlaybackSpeed />
            </div>
          </SettingsCard>
        </Flex>
      </Overlay>
    </>
  );
};

export const Settings = () => (
  <SettingsProvider>
    <SettingsUI />
  </SettingsProvider>
);
